import { useRef, useCallback, useEffect } from "react";

interface KnobProps {
  value: number; // 0-1 normalized
  onChange: (value: number) => void;
  label: string;
  displayValue: string;
  size?: "sm" | "md";
}

export function Knob({ value, onChange, label, displayValue, size = "md" }: KnobProps) {
  const knobRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ active: false, startY: 0, startValue: 0 });

  const rotation = (value - 0.5) * 270; // -135 to +135 degrees

  const handleMove = useCallback(
    (clientY: number) => {
      if (!dragState.current.active) return;
      const delta = (dragState.current.startY - clientY) / 100;
      const newValue = Math.max(0, Math.min(1, dragState.current.startValue + delta));
      onChange(newValue);
    },
    [onChange]
  );

  const handleEnd = useCallback(() => {
    dragState.current.active = false;
    document.body.style.cursor = "";
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragState.current = { active: true, startY: e.clientY, startValue: value };
      document.body.style.cursor = "ns-resize";
    },
    [value]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      dragState.current = {
        active: true,
        startY: e.touches[0].clientY,
        startValue: value,
      };
    },
    [value]
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientY);
    const onTouchMove = (e: TouchEvent) => {
      if (dragState.current.active) {
        e.preventDefault();
        handleMove(e.touches[0].clientY);
      }
    };
    const onMouseUp = () => handleEnd();
    const onTouchEnd = () => handleEnd();

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [handleMove, handleEnd]);

  const sizeClasses = size === "sm" ? "w-7 h-7" : "w-10 h-10";
  const indicatorTop = size === "sm" ? "top-[4px]" : "top-[6px]";
  const indicatorH = size === "sm" ? "h-[6px]" : "h-[8px]";

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        ref={knobRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className={`${sizeClasses} rounded-full cursor-ns-resize relative select-none touch-none transition-shadow hover:shadow-[0_0_12px_rgba(0,255,136,0.3)]`}
        style={{
          background: "linear-gradient(145deg, #2a2a2a, #1a1a1a)",
          boxShadow: "0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)",
          transform: `rotate(${rotation}deg)`,
        }}
      >
        {/* Inner circle */}
        <div
          className="absolute inset-1 rounded-full"
          style={{ background: "linear-gradient(145deg, #222, #111)" }}
        />
        {/* Indicator */}
        <div
          className={`absolute ${indicatorTop} left-1/2 -translate-x-1/2 w-0.5 ${indicatorH} rounded-sm`}
          style={{
            background: "#00ff88",
            boxShadow: "0 0 4px rgba(0,255,136,0.5)",
          }}
        />
      </div>
      <span className="text-[8px] uppercase tracking-wide text-[#555]">{label}</span>
      <span className="text-[9px] font-semibold text-[#00ff88]">{displayValue}</span>
    </div>
  );
}
